import Typography from "@mui/material/Typography";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { useAchievements } from "api/achievements";
import { useEvents } from "api/events";

import { EmptyState } from "components/EmptyState/EmptyState";
import { ReactComponent as FilterFunnel01Icon } from "components/Icons/filter-funnel-01.svg";
import { AttributesDetails } from "components/Layout/CustomEvent/SingleEvent/AttributesDetails/AttributesDetails";
import { createHeadItems, OLTable } from "components/Table/OLTable";
import { calculateCount } from "components/Table/tableHelpers";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import { uniq } from "lodash";
import { useNavigate } from "react-router-dom";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import { USER_TRIGGERS_HISTORY } from "state/tables/types";
import { TriggeredAchievementListItem } from "types/achievements";
import { ListingActions, ListResponse } from "types/listing";
import { OLDate } from "utilities/dateFormat";
import { links } from "utilities/links";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    eventNameRow: {
      alignItems: "center",
      display: "flex",
      gap: theme.spacing(5),
    },
    emptyState: {
      background: theme.gray50,
      borderRadius: "8px",
    },
  }),
);

export function TriggeredCustomEvents({
  achievementRuleId,
  memberID,
}: {
  achievementRuleId: string;
  memberID: string;
}) {
  const classes = useStyles();
  const navigate = useNavigate();
  const { message } = useMessage();
  const { listing, updateListing } = useListing();
  const { setColumnOrdering } = useTablesStateHelpers();

  const { useMembersTriggeredAchievements } = useAchievements();

  const { data: achievementsList } = useMembersTriggeredAchievements(
    memberID as string,
    achievementRuleId
      ? {
          filters: [
            {
              condition: "",
              query: "ruleId",
              value: achievementRuleId,
            },
          ],
          page: listing.page,
          rows: listing.rowsPerPage,
        }
      : undefined,
  );
  const selectedTriggerIds = uniq(
    achievementsList?.items?.map(({ triggerId }) => triggerId),
  ).join();

  const { useListOfCustomEvents } = useEvents();
  const { data: listOfCustomEvents } = useListOfCustomEvents({
    filters: selectedTriggerIds
      ? [
          {
            condition: "in",
            query: "customEventId",
            value: selectedTriggerIds,
          },
        ]
      : undefined,
    page: listing.page,
    rows: listing.rowsPerPage,
  }) as { data: ListResponse };

  const headItems = createHeadItems([
    {
      headerText: message({ id: "customEventDate" }),
      property: "eventDate",
    },
    {
      headerText: message({ id: "customEventName" }),
      property: "eventName",
    },
    {
      headerText: message({ id: "customEventId" }),
      property: "customEventId",
    },
  ]);

  const items = achievementsList?.items
    ? createItems(achievementsList.items)
    : [];

  function createItems(items: TriggeredAchievementListItem[]) {
    return items.map((item) => {
      const selectedItemValues = listOfCustomEvents?.items?.find(
        ({ customEventId }) => customEventId === item?.triggerId,
      );

      return {
        id: selectedItemValues?.customEventId,
        cells: [
          {
            content: OLDate(selectedItemValues?.eventDate),
            property: "eventDate",
          },
          {
            content: (
              <div className={classes.eventNameRow}>
                {selectedItemValues?.eventName}
                {selectedItemValues &&
                Object.keys(selectedItemValues?.body)?.length ? (
                  <span className={classes.eventNameRow}>
                    <AttributesDetails
                      attributes={selectedItemValues?.body}
                      showCount={3}
                    />
                  </span>
                ) : null}
              </div>
            ),
            property: "eventName",
          },
          {
            content: selectedItemValues?.customEventId,
            property: "customEventId",
          },
        ],
      };
    });
  }

  return (
    <>
      <Typography mb={6}>{message({ id: "triggersHistory" })}</Typography>
      <Typography fontSize={12} mb={4}>
        {message({ id: "triggersHistoryInfo" })}
      </Typography>
      {achievementsList?.items?.length === 0 ? (
        <EmptyState
          className={classes.emptyState}
          description={message({ id: "noActivityYetInfo" })}
          icon={<FilterFunnel01Icon />}
          title={message({ id: "noActivityYet" })}
        />
      ) : (
        <OLTable
          disableColumnManager
          setColumnOrdering={setColumnOrdering}
          tableName={USER_TRIGGERS_HISTORY}
          actions={{
            body: [
              {
                action: (
                  id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
                ) => navigate(links.customEvent(id as string)),
                label: message({ id: "showDetails" }),
              },
            ],
          }}
          data-test-id="user-triggers-history"
          columns={achievementsList && headItems}
          onSort={() => {}}
          rows={achievementsList && items}
          sort={{
            order: listing.order,
            property: listing.property,
            rowsPerPage: listing.rowsPerPage,
            page: listing.page,
          }}
          pagination={{
            count: calculateCount(
              achievementsList?.total.filtered,
              achievementsList?.total.all,
            ),
            onChangePage: (page) =>
              updateListing({
                payload: page,
                type: ListingActions.CHANGE_PAGE,
              }),
            onChangeRowsPerPage: (rows) =>
              updateListing({
                payload: rows,
                type: ListingActions.CHANGE_ROWS_PER_PAGE,
              }),
            page: listing.page,
            rowsPerPage: listing.rowsPerPage,
          }}
        />
      )}
    </>
  );
}
